import { useCallback, useState, useEffect, useMemo } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import Drawer, { selectSelectedDrawer } from "../../../organisms/Drawer";
import { actions } from "../../../organisms/Drawer/drawers.reducers";
import {
  EMPTY_STRING,
  BUTTON_TEXT,
  INDEX,
  REGEX,
  EMPTY_ARRAY,
  VALIDATION_MESSAGES,
} from "../../../../constants";
import {
  createCollection,
  selectSelectedDataModelId,
  updateCollection,
  selectCollection,
} from "../../../../pages/DataModels";
import { Table } from "../../../molecules";
import { DRAWER_SIZE, INTERFACES } from "../drawers.constants";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import { actions as modalActions } from "../../Modal/modal.reducers";
import {
  databaseIcon,
  documentIcon,
  filesIcon,
} from "../../../../assets/icons";
import { MODALS } from "../../AppModals/modals.constants";

const { CREATE_COLLECTION, UPDATE_COLLECTION } = SPINNER_NAMES;
const {
  SPECIAL_CHARACTERS,
  SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
  SPECIAL_CHARACTERS_WHITESPACES_AT_END,
  SPECIAL_CHARACTERS_WHITESPACE_IN_BETWEEN,
} = REGEX;
const { setSelectedDrawer } = actions;
const { setSelectedModal } = modalActions;
const { CONTINUE, SUBMIT } = BUTTON_TEXT;
const START_FROM_SCRATCH = "Start from Scratch";
const CANCEL = "Cancel";
const PREVIOUS = "Previous";
const { PROPERTY_NAME } = REGEX;
const { FIRST, SECOND } = INDEX;
const { FULL } = DRAWER_SIZE;
const { DATE_TIME } = INTERFACES;
const {
  CHECK_PROPERTY_NAME,
  COLLECTION_REQUIRED,
  NAME_SHOULD_CONTAIN,
  NAME_SHOULD_NOT_CONTAIN,
  NAME_SHOULD_NOT_CONTAIN_AT_END,
  MIN_CHAR_LENGTH,
  PRIMARY_KEY,
  PRIMARY_KEY_REQUIRED,
  VALID_PRIMARY_KEY,
  MAX_CHAR_LENGTH_TWENTY_ONE,
  NAME_SHOULD_NOT_CONTAIN_WHITESPACE_IN_BETWEEN,
} = VALIDATION_MESSAGES;
const { FILE_IMPORT_MODAL, CREATE_COLLECTION_MODAL, IMPORT_DATAMODAL_MODAL } =
  MODALS;

const ManageCollectionDrawer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedDrawer = useSelector(selectSelectedDrawer);
  const dataModelId = useSelector(selectSelectedDataModelId);
  const selectedCollection = useSelector(selectCollection);

  const [selectedCollectionTemplate, setSelectedCollectionTemplate] = useState({
    name: EMPTY_STRING,
    properties: [],
  });
  const [currentStep, setCurrentStep] = useState(FIRST);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [initialValues, setInitialValues] = useState({
    name: EMPTY_STRING,
    primaryKey: EMPTY_STRING,
  });

  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const COLLECTION_DRAWER_ERROR = "Unable to update the schema";

  const COLLECTION_CREATION_OPTIONS = [
    {
      key: "fromScratch",
      icon: filesIcon,
      title: "Create From Wizard",
      subtitle:
        "Create Collection from scratch by adding properties as you want.",
      handleOnClick: () => {
        dispatch(setSelectedModal(CREATE_COLLECTION_MODAL));
      },
    },
    {
      key: "uploadFile",
      icon: documentIcon,
      title: "Create from Excel File",
      subtitle: "Upload Your own CSV ,XLSX or XLS file and edit directly.",
      handleOnClick: () => {
        dispatch(setSelectedModal(FILE_IMPORT_MODAL));
      },
    },
    {
      key: "selectApp",
      icon: databaseIcon,
      title: "Create from Datamodel template",
      subtitle:
        "Get the collections, endpoints, services based on selected template",
      handleOnClick: () => {
        dispatch(setSelectedModal(IMPORT_DATAMODAL_MODAL));
      },
    },
  ];

  useEffect(() => {
    if (selectedCollection) {
      setInitialValues((prev) => ({ ...prev, ...selectedCollection }));
      const filteredProperties = selectedCollection.schema.properties.filter(
        (each) => !each.meta.isKey
      );
      setFilteredProperties(filteredProperties);
      setSelectedCollectionTemplate({
        name: selectedCollection.name,
        properties: selectedCollection.schema.properties,
      });
      setSelectedProperties(
        selectedCollection.schema.properties.filter((each) => !each.meta.isKey)
      );
      setCurrentStep(SECOND);
    }
  }, [selectedCollection]);

  useEffect(() => {
    const { name, properties } = selectedCollectionTemplate;
    const primaryKeyField = properties.find((each) => each.meta.isKey);
    if (name && !selectedCollection && primaryKeyField) {
      const filteredProperties = properties.filter((each) => !each.meta.isKey);
      setFilteredProperties(filteredProperties);
      if (name === START_FROM_SCRATCH)
        setInitialValues((prev) => ({
          ...prev,
          name: EMPTY_STRING,
          primaryKey: primaryKeyField.schema.key,
        }));
      else
        setInitialValues((prev) => ({
          ...prev,
          name,
          primaryKey: primaryKeyField.schema.key,
        }));
    }
  }, [selectedCollectionTemplate, selectedCollection]);

  const isSelectButtonDisabled = useMemo(() => {
    return (
      isEqual(new Set(filteredProperties), new Set(selectedProperties)) &&
      selectedCollection
    );
  }, [filteredProperties, selectedProperties, selectedCollection]);

  const validationSchema = Yup.object({
    name: Yup.string()
      .test(
        CHECK_PROPERTY_NAME,
        COLLECTION_REQUIRED,
        (val) => currentStep === FIRST || val
      )
      .matches(
        SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
        NAME_SHOULD_CONTAIN
      )
      .matches(SPECIAL_CHARACTERS, NAME_SHOULD_NOT_CONTAIN)
      .min(3, MIN_CHAR_LENGTH)
      .max(21, MAX_CHAR_LENGTH_TWENTY_ONE)
      .matches(
        SPECIAL_CHARACTERS_WHITESPACES_AT_END,
        NAME_SHOULD_NOT_CONTAIN_AT_END
      )
      .matches(
        SPECIAL_CHARACTERS_WHITESPACE_IN_BETWEEN,
        NAME_SHOULD_NOT_CONTAIN_WHITESPACE_IN_BETWEEN
      ),
    primaryKey: Yup.string()
      .test(
        PRIMARY_KEY,
        PRIMARY_KEY_REQUIRED,
        (val) => currentStep === FIRST || val
      )
      .matches(PROPERTY_NAME, VALID_PRIMARY_KEY),
  });

  const handleAllPropertySelection = (e, rows) => {
    const selectedRows = e.target.checked ? rows : EMPTY_ARRAY;
    setSelectedProperties(selectedRows);
  };

  const handleSinglePropertySelection = (e, clickedProperty) => {
    if (e.target.checked)
      setSelectedProperties([...selectedProperties, clickedProperty]);
    else
      setSelectedProperties(
        selectedProperties.filter(
          (property) => property.name !== clickedProperty.name
        )
      );
  };

  const headerFormatter = (rows) => {
    const isCheckedAll =
      filteredProperties.length === selectedProperties.length;
    return (
      <div>
        <input
          type="checkbox"
          value={rows}
          defaultChecked={isCheckedAll}
          className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
          onClick={(e) => handleAllPropertySelection(e, rows)}
        />
      </div>
    );
  };

  const propertyFormatter = (row) => (
    <div className="flex items-center mx-3">
      <input
        type="checkbox"
        value={row}
        checked={selectedProperties.some((each) => each.name === row.name)}
        className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer mr-2"
        onChange={(e) => {
          handleSinglePropertySelection(e, row);
        }}
      />
      {row.name}
    </div>
  );

  const dataTypeFormatter = (row) => row.meta.description;

  const columns = [
    {
      formatter: propertyFormatter,
      headerFormatter: headerFormatter,
      text: "Name",
    },
    {
      text: "Type",
      className: "text-center",
      formatter: dataTypeFormatter,
    },
  ];

  const handleClose = useCallback(() => {
    dispatch(setSelectedDrawer(null));
    setCurrentStep(FIRST);
  }, [dispatch]);

  const handleOnSubmit = useCallback(
    (values) => {
      const { properties } = selectedCollectionTemplate;
      const primaryKeyField = properties.find((each) => each.meta.isKey);
      const formattedPrimaryKey = values.primaryKey
        .toLowerCase()
        .replaceAll(" ", "_");

      const updatedProperties = structuredClone(selectedProperties).map(
        (property) => {
          if (property.meta.interface === DATE_TIME)
            property.schema.defaultValue = new Date().toISOString();
          return property;
        }
      );

      const updatedCollection = {
        ...values,
        dataModelId: dataModelId,
        primaryKey: formattedPrimaryKey,
        schema: {
          properties: [
            ...updatedProperties,
            primaryKeyField && {
              ...primaryKeyField,
              name: values.primaryKey,
              schema: {
                ...primaryKeyField.schema,
                key: formattedPrimaryKey,
              },
            },
          ],
        },
      };
      dispatch(
        selectedCollection
          ? updateCollection({
              id: updatedCollection.id,
              data: {
                changeSet: updatedCollection,
                collection: {
                  ...selectedCollection,
                },
                operation: "update",
              },
            })
          : createCollection(updatedCollection)
      ).then((res) => {
        if (res.payload) {
          handleClose();
          navigate(
            `/data-models/${dataModelId}/collections/${res.payload.id}/details`,
            { replace: selectedCollection }
          );
        }
      });
    },
    [
      selectedCollection,
      dataModelId,
      dispatch,
      handleClose,
      selectedProperties,
      selectedCollectionTemplate,
      navigate,
    ]
  );

  const getButtonText = (step) => (step === FIRST ? CONTINUE : SUBMIT);

  const getDrawerHeading = (step) =>
    step === SECOND && selectedCollection
      ? "Manage Collection"
      : "Create Collection";

  const getDrawerSubHeading = (step) =>
    step === FIRST
      ? "Select how you'd like to create your collection"
      : "Finish creating your new collection";

  const checkShouldShowTemplates = (step) => step === FIRST;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={handleOnSubmit}
      validateOnMount={true}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleReset,
        isValid,
      }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            {!selectedCollection && (
              <button
                className="btn btn-primary-light mr-1"
                type="button"
                onClick={() => {
                  if (currentStep === FIRST) {
                    handleClose();
                    handleReset();
                  } else {
                    setSelectedProperties(EMPTY_ARRAY);
                    setCurrentStep(FIRST);
                  }
                }}
              >
                {currentStep === FIRST ? CANCEL : PREVIOUS}
              </button>
            )}
            {currentStep === SECOND && (
              <button
                className="btn btn-primary"
                type={SUBMIT}
                disabled={isSpinnerActive || !isValid || isSelectButtonDisabled}
              >
                <Spinner
                  name={
                    selectedCollection ? UPDATE_COLLECTION : CREATE_COLLECTION
                  }
                  setIsSpinnerActive={setIsSpinnerActive}
                  persistSize
                >
                  <>{getButtonText(currentStep)}</>
                </Spinner>
              </button>
            )}
          </div>
        );
        return (
          <Form>
            <Drawer
              position="right"
              size={FULL}
              heading={getDrawerHeading(currentStep)}
              subHeading={getDrawerSubHeading(currentStep)}
              selectedDrawer={selectedDrawer}
              handleClose={handleClose}
              handleReset={handleReset}
              footer={footer}
              alertErrorMessage={COLLECTION_DRAWER_ERROR}
            >
              {checkShouldShowTemplates(currentStep) ? (
                <div className="scrollbar flex-1 overflow-auto p-6">
                  <div>
                    {COLLECTION_CREATION_OPTIONS.map((each) => (
                      <div
                        className="col-span-6 md:col-span-12 lg:col-span-12 mb-4"
                        onClick={() => {
                          each.handleOnClick();
                        }}
                        key={each.title}
                      >
                        <div className="w-full flex items-center bg-gray-900 rounded-lg hover:ring-2 hover:ring-indigo-700 hover:shadow-md hover:shadow-indigo-700 hover:bg-gray-800 cursor-pointer md:p-5">
                          <div className="icon mr-4">
                            <img
                              src={each.icon}
                              alt="contact"
                              className="mx-auto w-28 h-20"
                            />
                          </div>
                          <div className="flex-col items-center">
                            <div className="text-lg font-medium text-gray-300">
                              {each.title}
                            </div>
                            <div className="text-sm font-medium text-gray-400 mt-2">
                              {each.subtitle}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                values && (
                  <div className="scrollbar flex-1 overflow-auto no-scrollbar">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-4 p-4">
                        <div className="form-group">
                          <label className="text-sm font-medium text-gray-50 flex">
                            Collection Name
                          </label>
                          <Field
                            type="text"
                            value={values.name}
                            name="name"
                            className={classNames("form-control border-2", {
                              "border-red-600": touched.name && errors.name,
                            })}
                            placeholder="Collection Name"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={selectedCollection}
                          />
                          {touched.name && errors.name && (
                            <div className="text-sm font-medium text-red-500">
                              {errors.name}
                            </div>
                          )}
                        </div>
                        <div className="form-group">
                          <label className="text-sm font-medium text-gray-50 flex">
                            Primary Key
                          </label>
                          <Field
                            type="text"
                            value={values.primaryKey}
                            name="primaryKey"
                            className={classNames("form-control border-2", {
                              "border-red-600":
                                touched.primaryKey && errors.primaryKey,
                            })}
                            placeholder="Collection ID"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!!selectedCollection}
                          />
                          {touched.primaryKey && errors.primaryKey && (
                            <div className="text-sm font-medium text-red-500">
                              {errors.primaryKey}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {(selectedCollection ||
                      selectedCollectionTemplate.name !==
                        START_FROM_SCRATCH) && (
                      <div className="p-4">
                        <label className="text-sm font-medium text-gray-50 flex">
                          Select Properties to Add
                        </label>
                        <div>
                          <Table columns={columns} data={filteredProperties} />
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}
            </Drawer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ManageCollectionDrawer;
